import type {
    PolicyDocument,
    PolicyElement,
    PolicyImport,
    RegoValue,
    Condition,
    ElseClause,
    ComparisonOperator,
} from '@/types/policy-editor';

const LOG_PREFIX = 'rego-parser';

let _idCounter = 0;
function nextId(): string {
    return `el-${++_idCounter}`;
}
function resetIds(): void {
    _idCounter = 0;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function parseRego(source: string): PolicyDocument {
    resetIds();

    const lines = source.split(/\r?\n/);
    const packagePath = extractPackage(lines);
    const imports = extractImports(lines);
    const remaining = stripHeaderLines(lines);
    const blocks = splitBlocks(remaining);
    const elements = blocks.map(classifyBlock).filter(Boolean) as PolicyElement[];

    return { packagePath, imports, elements };
}

// ---------------------------------------------------------------------------
// Package & Imports
// ---------------------------------------------------------------------------

function extractPackage(lines: string[]): string {
    for (const line of lines) {
        const m = line.match(/^\s*package\s+(.+)/);
        if (m) return m[1].trim();
    }
    return '';
}

function extractImports(lines: string[]): PolicyImport[] {
    const result: PolicyImport[] = [];
    for (const line of lines) {
        const m = line.match(/^\s*import\s+(\S+)(?:\s+as\s+(\S+))?/);
        if (m) {
            result.push({ path: m[1], ...(m[2] ? { alias: m[2] } : {}) });
        }
    }
    return result;
}

function stripHeaderLines(lines: string[]): string[] {
    return lines.filter(line => {
        const trimmed = line.trimStart();
        return !trimmed.startsWith('package ') && !trimmed.startsWith('import ');
    });
}

// ---------------------------------------------------------------------------
// Block splitting
// ---------------------------------------------------------------------------

interface RawBlock {
    comments: string[];
    body: string;
}

function splitBlocks(lines: string[]): RawBlock[] {
    const blocks: RawBlock[] = [];
    let currentComments: string[] = [];
    let currentBody: string[] = [];
    let braceDepth = 0;
    let inBlock = false;

    function flush() {
        const bodyText = currentBody.join('\n').trim();
        if (bodyText) {
            blocks.push({ comments: currentComments, body: bodyText });
        }
        currentComments = [];
        currentBody = [];
        inBlock = false;
        braceDepth = 0;
    }

    for (const line of lines) {
        const trimmed = line.trim();

        if (!trimmed) {
            if (!inBlock || braceDepth === 0) {
                flush();
            } else {
                currentBody.push(line);
            }
            continue;
        }

        if (trimmed.startsWith('#') && !inBlock) {
            currentComments.push(trimmed.replace(/^#\s*/, ''));
            continue;
        }

        if (!inBlock && currentBody.length > 0 && braceDepth === 0 && looksLikeNewRule(trimmed)) {
            flush();
        }

        inBlock = true;
        currentBody.push(line);

        braceDepth += countChar(line, '{') - countChar(line, '}');
        if (braceDepth < 0) braceDepth = 0;
    }

    flush();
    return blocks;
}

function looksLikeNewRule(line: string): boolean {
    if (/^\s*default\s+/.test(line)) return true;
    if (/^[a-zA-Z_]\w*\s*[=:]/.test(line)) return true;
    if (/^[a-zA-Z_]\w*\s+if\s/.test(line)) return true;
    if (/^[a-zA-Z_]\w*\s+contains\s/.test(line)) return true;
    if (/^[a-zA-Z_]\w*\(/.test(line)) return true;
    if (/^[a-zA-Z_]\w*\[/.test(line)) return true;
    return false;
}

function countChar(s: string, ch: string): number {
    let n = 0;
    let inStr = false;
    let escape = false;
    for (const c of s) {
        if (escape) { escape = false; continue; }
        if (c === '\\') { escape = true; continue; }
        if (c === '"') { inStr = !inStr; continue; }
        if (!inStr && c === ch) n++;
    }
    return n;
}

// ---------------------------------------------------------------------------
// Block classification
// ---------------------------------------------------------------------------

function classifyBlock(block: RawBlock): PolicyElement | null {
    const { comments, body } = block;
    const description = comments.length > 0 ? comments.join('\n') : undefined;

    const stripped = stripInlineComments(body);

    const defaultEl = tryParseDefault(stripped, description);
    if (defaultEl) return defaultEl;

    const funcEl = tryParseFunction(stripped, description);
    if (funcEl) return funcEl;

    const setEl = tryParseSetRule(stripped, description);
    if (setEl) return setEl;

    const objEl = tryParseObjectRule(stripped, description);
    if (objEl) return objEl;

    const singleEl = tryParseSingleValueRule(stripped, description);
    if (singleEl) return singleEl;

    const constEl = tryParseConstant(stripped, description);
    if (constEl) return constEl;

    return {
        id: nextId(),
        elementType: 'rawRego',
        name: extractNameGuess(stripped),
        description,
        source: body,
    };
}

function stripInlineComments(body: string): string {
    return body
        .split('\n')
        .map(line => {
            let inStr = false;
            let escape = false;
            for (let i = 0; i < line.length; i++) {
                const ch = line[i];
                if (escape) { escape = false; continue; }
                if (ch === '\\') { escape = true; continue; }
                if (ch === '"') { inStr = !inStr; continue; }
                if (!inStr && ch === '#') return line.slice(0, i).trimEnd();
            }
            return line;
        })
        .join('\n')
        .trim() || body;
}

function extractNameGuess(body: string): string {
    const m = body.match(/^([a-zA-Z_]\w*)/);
    return m ? m[1] : 'unknown';
}

// ---------------------------------------------------------------------------
// Default rules: `default name = value` or `default name := value`
// ---------------------------------------------------------------------------

function tryParseDefault(body: string, description?: string): PolicyElement | null {
    const m = body.match(/^default\s+([a-zA-Z_]\w*)\s*:?=\s*([\s\S]+)$/);
    if (!m) return null;

    const name = m[1];
    const valueText = m[2].trim();
    const value = parseRegoValue(valueText);

    if (value.kind === 'scalar' || value.kind === 'object' || value.kind === 'array' || value.kind === 'set') {
        return {
            id: nextId(),
            elementType: 'constant',
            name,
            description,
            value,
            isDefault: true,
        };
    }

    return {
        id: nextId(),
        elementType: 'singleValueRule',
        name,
        description,
        value,
        conditions: [],
        isDefault: true,
    };
}

// ---------------------------------------------------------------------------
// Function: `name(params) = value if { ... }` or `name(params) := value if { ... }`
// ---------------------------------------------------------------------------

function tryParseFunction(body: string, description?: string): PolicyElement | null {
    const m = body.match(/^([a-zA-Z_]\w*)\(([^)]*)\)\s*:?=\s*([\s\S]+)$/);
    if (!m) return null;

    const name = m[1];
    const params = m[2].split(',').map(p => p.trim()).filter(Boolean);
    const rest = m[3];

    const { valuePart, conditionBody } = splitValueAndBody(rest);

    return {
        id: nextId(),
        elementType: 'function',
        name,
        description,
        parameters: params,
        returnValue: valuePart.trim(),
        conditions: conditionBody ? parseConditionBody(conditionBody) : [],
    };
}

// ---------------------------------------------------------------------------
// Set rule: `name contains expr if { ... }`
// ---------------------------------------------------------------------------

function tryParseSetRule(body: string, description?: string): PolicyElement | null {
    const m = body.match(/^([a-zA-Z_]\w*)\s+contains\s+(\S+)\s+if\s*\{([\s\S]*)\}\s*$/);
    if (!m) return null;

    return {
        id: nextId(),
        elementType: 'setRule',
        name: m[1],
        description,
        memberExpression: m[2],
        conditions: parseConditionBody(m[3]),
    };
}

// ---------------------------------------------------------------------------
// Object rule: `name[key] = value if { ... }`
// ---------------------------------------------------------------------------

function tryParseObjectRule(body: string, description?: string): PolicyElement | null {
    const m = body.match(/^([a-zA-Z_]\w*)\[([^\]]+)\]\s*:?=\s*([\s\S]+)$/);
    if (!m) return null;

    const name = m[1];
    const keyExpr = m[2];
    const rest = m[3];

    const { valuePart, conditionBody } = splitValueAndBody(rest);

    return {
        id: nextId(),
        elementType: 'objectRule',
        name,
        description,
        keyExpression: keyExpr,
        valueExpression: valuePart.trim(),
        conditions: conditionBody ? parseConditionBody(conditionBody) : [],
    };
}

// ---------------------------------------------------------------------------
// Single-value rule: `name = value if { ... }` or `name if { ... }`
// Handles `else = value if { ... }` chains via brace-aware scanning.
// ---------------------------------------------------------------------------

interface RuleClause {
    valuePart: string;
    conditionBody: string | null;
}

function tryParseSingleValueRule(body: string, description?: string): PolicyElement | null {
    // Boolean shorthand: `name if { ... }`
    const boolIfMatch = body.match(/^([a-zA-Z_]\w*)\s+if\s*\{([\s\S]*)\}\s*$/);
    if (boolIfMatch) {
        return {
            id: nextId(),
            elementType: 'singleValueRule',
            name: boolIfMatch[1],
            description,
            value: { kind: 'scalar', value: true },
            conditions: parseConditionBody(boolIfMatch[2]),
        };
    }

    const headMatch = body.match(/^([a-zA-Z_]\w*)\s*:?=\s*/);
    if (!headMatch) return null;

    const name = headMatch[1];
    const rest = body.slice(headMatch[0].length);

    const clauses = extractRuleClauses(rest);
    if (!clauses || clauses.length === 0) return null;

    const mainClause = clauses[0];
    if (!mainClause.conditionBody) return null;

    const value = parseRegoValue(mainClause.valuePart.trim());
    const conditions = parseConditionBody(mainClause.conditionBody);

    const elseChain: ElseClause[] = [];
    for (let i = 1; i < clauses.length; i++) {
        elseChain.push({
            id: nextId(),
            value: parseRegoValue(clauses[i].valuePart.trim()),
            conditions: clauses[i].conditionBody
                ? parseConditionBody(clauses[i].conditionBody!)
                : [],
        });
    }

    return {
        id: nextId(),
        elementType: 'singleValueRule',
        name,
        description,
        value,
        conditions,
        ...(elseChain.length > 0 ? { elseChain } : {}),
    };
}

/**
 * Scans text after `name = ` extracting (value, conditionBody) pairs,
 * handling `else = value if { ... }` chains with proper brace tracking.
 */
function extractRuleClauses(text: string): RuleClause[] | null {
    const clauses: RuleClause[] = [];
    let pos = 0;

    while (pos < text.length) {
        pos = skipWhitespace(text, pos);
        if (pos >= text.length) break;

        if (clauses.length > 0) {
            if (!text.slice(pos).startsWith('else')) break;
            pos += 4;
            const assignMatch = text.slice(pos).match(/^\s*:?=\s*/);
            if (assignMatch) {
                pos += assignMatch[0].length;
            } else {
                pos = skipWhitespace(text, pos);
            }
        }

        const restFromHere = text.slice(pos);
        const ifIdx = findTopLevelIf(restFromHere);

        if (ifIdx === -1) {
            clauses.push({ valuePart: restFromHere.trim(), conditionBody: null });
            break;
        }

        const valuePart = restFromHere.slice(0, ifIdx).trim();
        pos += ifIdx + 2;
        pos = skipWhitespace(text, pos);

        if (pos < text.length && text[pos] === '{') {
            pos++;
            const bodyStart = pos;
            let depth = 1;
            let inStr = false;
            let escape = false;

            while (pos < text.length && depth > 0) {
                const ch = text[pos];
                if (escape) { escape = false; pos++; continue; }
                if (ch === '\\') { escape = true; pos++; continue; }
                if (ch === '"') { inStr = !inStr; pos++; continue; }
                if (!inStr) {
                    if (ch === '{') depth++;
                    if (ch === '}') { depth--; if (depth === 0) break; }
                }
                pos++;
            }

            clauses.push({ valuePart, conditionBody: text.slice(bodyStart, pos) });
            pos++; // skip closing '}'
        } else {
            clauses.push({ valuePart, conditionBody: null });
            break;
        }
    }

    return clauses.length > 0 ? clauses : null;
}

function skipWhitespace(text: string, pos: number): number {
    while (pos < text.length && /\s/.test(text[pos])) pos++;
    return pos;
}

// ---------------------------------------------------------------------------
// Constant: `name = value` (no `if` keyword, no parentheses)
// ---------------------------------------------------------------------------

function tryParseConstant(body: string, description?: string): PolicyElement | null {
    if (/\bif\b/.test(body)) return null;

    const m = body.match(/^([a-zA-Z_]\w*)\s*:?=\s*([\s\S]+)$/);
    if (!m) return null;

    const name = m[1];
    const value = parseRegoValue(m[2].trim());

    return {
        id: nextId(),
        elementType: 'constant',
        name,
        description,
        value,
    };
}

// ---------------------------------------------------------------------------
// Value splitting: separate value from `if { conditions }`
// Handles values that themselves contain braces (large objects).
// ---------------------------------------------------------------------------

function splitValueAndBody(rest: string): { valuePart: string; conditionBody: string | null } {
    const ifIdx = findTopLevelIf(rest);
    if (ifIdx === -1) {
        return { valuePart: rest, conditionBody: null };
    }

    const valuePart = rest.slice(0, ifIdx);
    const afterIf = rest.slice(ifIdx + 2).trim();

    const bodyMatch = afterIf.match(/^\{([\s\S]*)\}\s*$/);
    if (bodyMatch) {
        return { valuePart, conditionBody: bodyMatch[1] };
    }

    return { valuePart, conditionBody: afterIf };
}

/**
 * Finds the index of the top-level `if` keyword (brace depth 0, not inside
 * a string, preceded by whitespace or start-of-string, followed by
 * whitespace or `{`).
 */
function findTopLevelIf(text: string): number {
    let depth = 0;
    let inStr = false;
    let escape = false;

    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (escape) { escape = false; continue; }
        if (ch === '\\') { escape = true; continue; }
        if (ch === '"') { inStr = !inStr; continue; }
        if (inStr) continue;
        if (ch === '{' || ch === '[' || ch === '(') { depth++; continue; }
        if (ch === '}' || ch === ']' || ch === ')') { depth--; continue; }

        if (depth === 0 && ch === 'i' && text[i + 1] === 'f') {
            const before = i === 0 ? ' ' : text[i - 1];
            const after = text[i + 2];
            if (/\s/.test(before) && (!after || /[\s{]/.test(after))) {
                return i;
            }
        }
    }

    return -1;
}

// ---------------------------------------------------------------------------
// Condition body parsing
// ---------------------------------------------------------------------------

function parseConditionBody(bodyText: string): Condition[] {
    const lines = bodyText
        .split('\n')
        .map(l => l.trim())
        .filter(l => l && !l.startsWith('#'));

    const conditions: Condition[] = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];

        if (line.startsWith('every ') && line.endsWith('{') && !line.endsWith('}')) {
            const everyResult = collectMultiLineEvery(lines, i);
            if (everyResult) {
                conditions.push(everyResult.condition);
                i = everyResult.nextIndex;
                continue;
            }
        }

        conditions.push(parseConditionLine(line));
        i++;
    }

    return conditions;
}

/**
 * Collects a multi-line `every var in collection { body }` block
 * starting at `startIndex`, tracking brace depth to find the closing `}`.
 */
function collectMultiLineEvery(
    lines: string[],
    startIndex: number,
): { condition: Condition; nextIndex: number } | null {
    const firstLine = lines[startIndex];
    const m = firstLine.match(/^every\s+(.+?)\s+in\s+(.+?)\s*\{$/);
    if (!m) return null;

    const vars = m[1].split(',').map(v => v.trim());
    const collection = m[2];

    let depth = 1;
    const bodyLines: string[] = [];

    for (let j = startIndex + 1; j < lines.length; j++) {
        const l = lines[j];
        depth += countChar(l, '{') - countChar(l, '}');

        if (depth <= 0) {
            const inner = parseConditionBody(bodyLines.join('\n'));
            return {
                condition: { type: 'universal', variables: vars, collection, body: inner },
                nextIndex: j + 1,
            };
        }

        bodyLines.push(l);
    }

    return null;
}

function parseConditionLine(line: string): Condition {
    if (line.startsWith('every ')) {
        return tryParseEvery(line) ?? { type: 'raw', text: line };
    }

    if (line.startsWith('some ')) {
        return tryParseSome(line) ?? { type: 'raw', text: line };
    }

    if (line.startsWith('not ')) {
        const inner = line.slice(4).trim();
        const inMatch = inner.match(/^(.+?)\s+in\s+(.+)$/);
        if (inMatch) {
            return { type: 'membership', term: inMatch[1], collection: inMatch[2], negated: true };
        }
        return { type: 'negation', expression: inner };
    }

    const assignFuncMatch = line.match(/^([a-zA-Z_]\w*)\s*:=\s*([a-zA-Z_][\w.]*)\((.+)\)$/);
    if (assignFuncMatch) {
        return {
            type: 'functionCall',
            assignTo: assignFuncMatch[1],
            name: assignFuncMatch[2],
            args: splitArgs(assignFuncMatch[3]),
        };
    }

    const assignMatch = line.match(/^([a-zA-Z_]\w*)\s*:=\s*(.+)$/);
    if (assignMatch) {
        return { type: 'assignment', variable: assignMatch[1], expression: assignMatch[2] };
    }

    const compMatch = line.match(/^(.+?)\s*(==|!=|<=|>=|<|>)\s*(.+)$/);
    if (compMatch) {
        return {
            type: 'comparison',
            left: compMatch[1].trim(),
            operator: compMatch[2] as ComparisonOperator,
            right: compMatch[3].trim(),
        };
    }

    const memberMatch = line.match(/^(.+?)\s+in\s+(.+)$/);
    if (memberMatch && !memberMatch[1].startsWith('some ')) {
        return { type: 'membership', term: memberMatch[1], collection: memberMatch[2] };
    }

    const bareFuncMatch = line.match(/^([a-zA-Z_][\w.]*)\((.+)\)$/);
    if (bareFuncMatch) {
        return {
            type: 'functionCall',
            name: bareFuncMatch[1],
            args: splitArgs(bareFuncMatch[2]),
        };
    }

    return { type: 'raw', text: line };
}

function tryParseSome(line: string): Condition | null {
    const m = line.match(/^some\s+(.+?)\s+in\s+(.+)$/);
    if (m) {
        const vars = m[1].split(',').map(v => v.trim());
        return { type: 'iteration', variables: vars, collection: m[2] };
    }
    return null;
}

function tryParseEvery(line: string): Condition | null {
    const m = line.match(/^every\s+(.+?)\s+in\s+(.+?)\s*\{([\s\S]*)\}$/);
    if (!m) return null;

    const vars = m[1].split(',').map(v => v.trim());
    const collection = m[2];
    const inner = parseConditionBody(m[3]);

    return { type: 'universal', variables: vars, collection, body: inner };
}

function splitArgs(argsText: string): string[] {
    const args: string[] = [];
    let depth = 0;
    let current = '';
    let inStr = false;
    let escape = false;

    for (const ch of argsText) {
        if (escape) { escape = false; current += ch; continue; }
        if (ch === '\\') { escape = true; current += ch; continue; }
        if (ch === '"') { inStr = !inStr; current += ch; continue; }
        if (inStr) { current += ch; continue; }
        if (ch === '(' || ch === '[' || ch === '{') { depth++; current += ch; continue; }
        if (ch === ')' || ch === ']' || ch === '}') { depth--; current += ch; continue; }
        if (ch === ',' && depth === 0) {
            args.push(current.trim());
            current = '';
            continue;
        }
        current += ch;
    }

    if (current.trim()) args.push(current.trim());
    return args;
}

// ---------------------------------------------------------------------------
// Value parsing
// ---------------------------------------------------------------------------

function parseRegoValue(text: string): RegoValue {
    const trimmed = text.trim();

    if (trimmed === 'true') return { kind: 'scalar', value: true };
    if (trimmed === 'false') return { kind: 'scalar', value: false };
    if (trimmed === 'null') return { kind: 'scalar', value: null };

    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
        return { kind: 'scalar', value: Number(trimmed) };
    }

    if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
        try {
            return { kind: 'scalar', value: JSON.parse(trimmed) };
        } catch {
            return { kind: 'raw', text: trimmed };
        }
    }

    if (trimmed === 'set()') {
        return { kind: 'set', items: [] };
    }

    if (trimmed.startsWith('[')) {
        return tryParseArrayValue(trimmed);
    }

    if (trimmed.startsWith('{')) {
        return tryParseObjectOrSetValue(trimmed);
    }

    if (/^[a-zA-Z_][\w.]*\(/.test(trimmed)) {
        return tryParseFunctionCallValue(trimmed);
    }

    if (/^[a-zA-Z_][\w.]*(?:\[[^\]]+\])*(?:\.[a-zA-Z_]\w*)*$/.test(trimmed)) {
        return { kind: 'reference', path: trimmed };
    }

    return { kind: 'raw', text: trimmed };
}

function tryParseArrayValue(text: string): RegoValue {
    try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) {
            return {
                kind: 'array',
                items: parsed.map(item => jsonToRegoValue(item)),
            };
        }
    } catch { /* fall through */ }

    return { kind: 'raw', text };
}

function tryParseObjectOrSetValue(text: string): RegoValue {
    if (isRegoSetLiteral(text)) {
        return parseRegoSet(text);
    }

    try {
        const parsed = JSON.parse(text);
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            return jsonObjToRegoValue(parsed);
        }
    } catch { /* fall through */ }

    const normalized = normalizeRegoObjectForJson(text);
    if (normalized !== text) {
        try {
            const parsed = JSON.parse(normalized);
            if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                return jsonObjToRegoValue(parsed);
            }
        } catch { /* fall through */ }
    }

    return { kind: 'raw', text };
}

/**
 * Heuristic: a `{...}` literal is a Rego set if none of its top-level
 * items contain a `:` (which would indicate object key-value pairs).
 */
function isRegoSetLiteral(text: string): boolean {
    const inner = text.slice(1, -1).trim();
    if (!inner) return false;

    let depth = 0;
    let inStr = false;
    let escape = false;
    let hasTopLevelColon = false;

    for (const ch of inner) {
        if (escape) { escape = false; continue; }
        if (ch === '\\') { escape = true; continue; }
        if (ch === '"') { inStr = !inStr; continue; }
        if (inStr) continue;
        if (ch === '{' || ch === '[' || ch === '(') { depth++; continue; }
        if (ch === '}' || ch === ']' || ch === ')') { depth--; continue; }
        if (depth === 0 && ch === ':') { hasTopLevelColon = true; break; }
    }

    return !hasTopLevelColon;
}

function parseRegoSet(text: string): RegoValue {
    const inner = text.slice(1, -1).trim();
    const items = splitArgs(inner);
    return {
        kind: 'set',
        items: items.map(item => parseRegoValue(item.trim())),
    };
}

function normalizeRegoObjectForJson(text: string): string {
    let result = text;
    result = result.replace(
        /([{,]\s*)([a-zA-Z_]\w*)\s*:/g,
        '$1"$2":'
    );
    result = result.replace(/,(\s*[}\]])/g, '$1');
    return result;
}

function jsonToRegoValue(val: unknown): RegoValue {
    if (val === null) return { kind: 'scalar', value: null };
    if (typeof val === 'string') return { kind: 'scalar', value: val };
    if (typeof val === 'number') return { kind: 'scalar', value: val };
    if (typeof val === 'boolean') return { kind: 'scalar', value: val };
    if (Array.isArray(val)) {
        return { kind: 'array', items: val.map(jsonToRegoValue) };
    }
    if (typeof val === 'object') {
        return jsonObjToRegoValue(val as Record<string, unknown>);
    }
    return { kind: 'raw', text: String(val) };
}

function jsonObjToRegoValue(obj: Record<string, unknown>): RegoValue {
    const entries = Object.entries(obj).map(([key, value]) => ({
        key,
        value: jsonToRegoValue(value),
    }));
    return { kind: 'object', entries };
}

function tryParseFunctionCallValue(text: string): RegoValue {
    const m = text.match(/^([a-zA-Z_][\w.]*)\((.+)\)$/);
    if (!m) return { kind: 'raw', text };

    return {
        kind: 'functionCall',
        name: m[1],
        args: splitArgs(m[2]).map(a => parseRegoValue(a.trim())),
    };
}
